import React from 'react';

import { LoadingOutlined, PlusSquareOutlined } from '@ant-design/icons';
import Modal from 'antd/es/modal/Modal';
import Upload from 'antd/es/upload/Upload';

import Button from '@common/react/components/Forms/Button';
import { FileInterface, FileType } from '@common/typescript/objects/FileInterface';

import ImageCropper from '@app/components/UI/PetMemorialModal/ImageCropper';
import '@app/scss/localCommon/components/newImageCropper.scss';

interface BaseFileProps {
	type: string;
	objectId?: number;
	onError?: (error: string) => void;
	fileType?: FileType;
	infoMessage?: string;
	asButton?: boolean;
	accept?: string;
	buttonCaption?: string | JSX.Element;
	buttonClassName?: string;
	disabled?: boolean;
	multiple?: boolean;
	noRelation?: boolean; // Whether to create relation for uploaded file or not
	additionalData?: object;
	transformData?: (data: FormData) => FormData; // Transform data before sending to server
}

interface FileProps extends BaseFileProps {
	update: (data: FileInterface) => void;
	maxFile: number;
	field: any;
	fileNote: string;
	showError?: boolean;
	aspectRatio?: number;
	imgExtensions?: Array<string>;
}

interface CustomRequestParams {
	action: any;
	data: any;
	file: any;
	filename: any;
	headers: any;
	onError: any;
	onProgress: any;
	onSuccess: any;
	withCredentials: any;
}

interface UploadFileParams {
	file: any;
	type: string;
	objectId: number;
	fileType: FileType;
	additionalData?: object;
	headers?: any;
}

export const uploadFile = (params: UploadFileParams, transformData?: (data: FormData) => FormData): Promise<any> => {
	const formData = new FormData();
	formData.append('file', params.file);
	formData.append('objectType', params.type);
	formData.append('objectId', params.objectId.toString());
	formData.append('fileType', params.fileType.toString());

	if (params.additionalData) {
		const data = params.additionalData;
		Object.keys(data).forEach((key) => formData.append(`data[${key}]`, data[key].toString()));
	}

	return fetch('fineUploader', {
		credentials: 'same-origin',
		method: 'POST',
		headers: params.headers,
		body: transformData ? transformData(formData) : formData,
	})
		.then((response) => response.json())
		.then((response) => {
			if (!response.success) throw new Error(response.response);
			return response.response;
		})
		.catch((error) => {
			console.log(error.message);
			throw error;
		});
};

const FileUpload: React.FC<FileProps> = (props) => {
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string>('');

	const [cropperModalOpen, setCropperModalOpen] = React.useState<boolean>(false);
	const [image, setImage] = React.useState<string>('');
	const [initFile, setInitFile] = React.useState<any>();

	const [uploadButton, setUploadButton] = React.useState<React.ReactNode>(null);

	const {
		asButton = false,
		accept = '',
		infoMessage = 'Image size must not exceed 1 Mb',
		buttonCaption = 'Upload',
		buttonClassName,
		showError = true,
		type,
		objectId = -1,
		fileType = FileType.Avatar,
		additionalData,
		noRelation,
		transformData,
		imgExtensions = ['jpg', 'jpeg', 'png'],
	} = props;

	const customRequest = (argument) => {
		const { file, headers } = argument as CustomRequestParams;

		setIsLoading(true);
		setError('');

		uploadFile(
			{
				file,
				fileType,
				objectId,
				type,
				additionalData,
				headers,
			},
			transformData,
		)
			.then((data: FileInterface) => {
				setIsLoading(false);
				props.update(data);
			})
			.catch((error) => {
				setIsLoading(false);
				setError(error.message);

				if (props.onError) {
					props.onError(error.message);
				}
			});
	};

	const beforeUpload = (file) => {
		const fileTypes = ['image/jpeg', 'image/jpg', 'image/png'];

		if (file.size < props.maxFile
			&& fileTypes.some((item) => item === file.type)
			&& imgExtensions.some((item) => file.name.toLowerCase().includes(item))
		) {
			setCropperModalOpen(true);
			setImage(file);
			setInitFile(file);
		}

		if (file.size > props.maxFile) {
			props.field.form.setFieldError('filePath', 'Image max file size is 10MB - Please upload a smaller image');
			props.field.form.setFieldTouched(props.field.field.name, true, false);
		}

		if (!fileTypes.some((item) => item === file.type) || !imgExtensions.some((item) => file.name.toLowerCase().includes(item))) {
			props.field.form.setFieldError('filePath', 'Image required to be in JPG or PNG format');
			props.field.form.setFieldTouched(props.field.field.name, true, false);
		}

		return false;
	};

	React.useEffect(() => {
		if (asButton) {
			setUploadButton(<>
				<Button type="button" className={buttonClassName} isLoading={isLoading} disabled={props.disabled}>
					{buttonCaption}
					<p className="note">{props.fileNote}</p>
				</Button>
				{showError && <div className="file-upload-error">{error}</div>}
			</>);
		} else {
			setUploadButton(<>
				{isLoading ? <LoadingOutlined /> : <PlusSquareOutlined />}
				<div className="ant-upload-text">{buttonCaption}</div>
				<div className="ant-upload-info">{(error.length && error) || infoMessage}</div>
			</>);
		}
	}, []);

	return (
		<>
			<Upload
				className={`avatar-uploader ${error.length ? 'avatar-uploader_error' : ''}`}
				listType={props.asButton ? 'text' : 'picture-card'}
				showUploadList={false}
				accept={accept}
				customRequest={customRequest}
				disabled={props.disabled}
				multiple={props.multiple}
				beforeUpload={beforeUpload}
			>
				{uploadButton}
			</Upload>

			<Modal
				wrapClassName="pet-mem-modal pet-mem-modal_form modal-wrap cropper-wrap"
				open={cropperModalOpen}
				onCancel={() => setCropperModalOpen(false)}
				width={1037}
				centered
			>
				<div>
					<ImageCropper
						image={image}
						setImage={setImage}
						initFile={initFile}
						setCroppedImage={(arg) => setInitFile(arg)}
						setCropperModal={(bool) => setCropperModalOpen(bool)}
						uploadFile={(arg) => customRequest(arg)}
						aspectRatio={props.aspectRatio}
					/>
				</div>
			</Modal>
		</>
	);
};

export default FileUpload;
